import { useCallback } from "react";
import type { ReactNode } from "react";
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import ControlledCommuneAutoComplete from "components/inputs/ControlledCommuneAutoComplete";
import EpciAutoComplete from "./EpciAutoComplete";
import CommunesInEpciSelect from "./ControlledCommunesInEpciSelect";
import type { CreateTransfertsModelForm } from "models";

function NewTransfertCollectivite(): ReactNode {
  const { setValue } = useFormContext<CreateTransfertsModelForm>();

  const [isCommuneDirectTransfert, idEpci, communeIds, idCommune] = useWatch<
    CreateTransfertsModelForm,
    ["isCommuneDirectTransfert", "idEpci", "communeIds", "idCommune"]
  >({
    name: ["isCommuneDirectTransfert", "idEpci", "communeIds", "idCommune"],
  });

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const reset = useCallback((): void => {
    setValue("idCommune", "");
    setValue("idEpci", "");
    setValue("communeIds", []);
  }, [setValue]);

  return (
    <Grid container spacing={2} size={12}>
      {((!isCommuneDirectTransfert && (idEpci === "" || communeIds?.length === 0)) ||
        (idCommune === "" && isCommuneDirectTransfert)) && (
        <Grid size={12}>
          <Typography color="error">
            Vous devez choisir soit une commune qui transfère directement, soit au moins une commune
            qui transfère via un EPCI.
          </Typography>
        </Grid>
      )}
      <Grid container spacing={2} sx={{ alignContent: "flex-start" }} size={{ xs: 12, md: 5.5 }}>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isCommuneDirectTransfert}
                onChange={(_, checked) => {
                  reset();
                  setValue("isCommuneDirectTransfert", checked);
                }}
              />
            }
            label="Je veux ajouter une commune qui transfère directement"
          />
        </Grid>
        <Grid size={12}>
          <ControlledCommuneAutoComplete
            name="idCommune"
            disabled={!isCommuneDirectTransfert}
            rules={{
              required: isCommuneDirectTransfert ? "Ce champ est obligatoire" : undefined,
            }}
            variant="id"
          />
        </Grid>
      </Grid>
      <Grid size={{ xs: 12, md: 1 }}>
        <Divider orientation={isSmallScreen ? undefined : "vertical"}>
          <Typography>OU</Typography>
        </Divider>
      </Grid>
      <Grid container spacing={2} sx={{ alignContent: "flex-start" }} size={{ xs: 12, md: 5.5 }}>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!isCommuneDirectTransfert}
                onChange={(_, checked) => {
                  reset();
                  setValue("isCommuneDirectTransfert", !checked);
                }}
              />
            }
            label="Je veux ajouter un groupe de commune liées à un EPCI"
          />
        </Grid>
        <Grid size={12}>
          <EpciAutoComplete
            name="idEpci"
            disabled={isCommuneDirectTransfert}
            rules={{
              required: isCommuneDirectTransfert ? undefined : "Ce champ est obligatoire",
            }}
          />
          <CommunesInEpciSelect
            name="communeIds"
            idEpci={idEpci}
            disabled={isCommuneDirectTransfert}
            rules={{
              required: isCommuneDirectTransfert
                ? undefined
                : "Vous devez sélectionner au moins une commune",
              validate: (value: Array<string>) => isCommuneDirectTransfert || value.length > 0,
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

export default NewTransfertCollectivite;
